'use client';

import { AlertTriangle, X } from 'lucide-react';

import { cn } from '@ui/lib/utils';

import { ResponsiveModal } from '../../../features/selection/responsiveModal';
import { Button } from '../../../ui/button';

export interface ApproveReviewConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isSubmitting?: boolean;
  title?: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  className?: string;
  confirmButtonClassName?: string;
  confirmButtonVariant?: 'primary' | 'success' | 'error';
}

export const ApproveReviewConfirmDialog = ({
  isOpen,
  onClose,
  onConfirm,
  isSubmitting = false,
  title = "You're about to Approve Review",
  description = 'Once approve this review will be visible to the platform under your activity',
  confirmLabel = 'Yes, Approve',
  cancelLabel = 'No, Check Once',
  className,
  confirmButtonVariant = 'primary'
}: ApproveReviewConfirmDialogProps) => {
  const getButtonClasses = () => {
    switch (confirmButtonVariant) {
      case 'success':
        return 'bg-green-500 hover:bg-green-600 text-white';
      case 'error':
        return 'bg-red-500 hover:bg-red-600 text-white';
      default:
        return '';
    }
  };

  return (
    <ResponsiveModal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      showCloseButton={false}
      className="flex flex-col items-center gap-4 p-6 text-center"
      dialogClassName="sm:max-w-sm p-0"
      drawerClassName="p-0 rounded-t-xl"
    >
      <div className={cn('flex flex-col items-center gap-4 w-full p-5', className)}>
        {/* Desktop close button */}
        <button
          type="button"
          onClick={onClose}
          className="cursor-pointer hidden md:block absolute top-4 right-4 shrink-0 text-neutral-500 hover:text-neutral-800 transition-colors"
        >
          <X className="size-5" />
        </button>

        <div className="flex size-14 items-center justify-center rounded-full bg-purple-50">
          <AlertTriangle className="size-7 text-purple-500" />
        </div>

        <h3 className="text-lg font-semibold text-neutral-800">{title}</h3>
        <p className="text-sm text-neutral-500 leading-relaxed">{description}</p>

        <div className="flex items-center justify-between md:justify-center gap-3 md:gap-4 w-full pt-2">
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className="cursor-pointer text-sm font-semibold text-primary hover:underline disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <Button onClick={onConfirm} disabled={isSubmitting} className={getButtonClasses()}>
            {isSubmitting ? '...' : confirmLabel}
          </Button>
        </div>
      </div>
    </ResponsiveModal>
  );
};
